import { type SolveStep } from './solve';
import { type FactorStep } from './factor';
import { type RewriteStep } from './simplify';
import { type SystemStep } from './system';
import { type InequalityStep } from './inequality';
import { type Expr } from './expr';
import { format } from './parse';
import { Fraction } from '@learnforge/demo-math/numeric';

export interface DescribeI18n {
  t(key: string, vars?: Record<string, string | number>): string;
}

/**
 * Describe a SolveStep with i18n support
 */
export function describeSolveStep(step: SolveStep, i18n: DescribeI18n): string {
  switch (step.rule) {
    case 'add': {
      const stepWithValue = step as { rule: 'add' | 'sub' | 'mul' | 'div'; value: Expr };
      const valueStr = formatValue(stepWithValue.value);
      return `${i18n.t('step.add')} ${valueStr}`.trim();
    }
    case 'sub': {
      const stepWithValue = step as { rule: 'add' | 'sub' | 'mul' | 'div'; value: Expr };
      const valueStr = formatValue(stepWithValue.value);
      return `${i18n.t('step.subtract')} ${valueStr}`.trim();
    }
    case 'mul': {
      const stepWithValue = step as { rule: 'add' | 'sub' | 'mul' | 'div'; value: Expr };
      const valueStr = formatValue(stepWithValue.value);
      return `${i18n.t('step.multiply')} ${valueStr}`.trim();
    }
    case 'div': {
      const stepWithValue = step as { rule: 'add' | 'sub' | 'mul' | 'div'; value: Expr };
      const valueStr = formatValue(stepWithValue.value);
      return `${i18n.t('step.divide')} ${valueStr}`.trim();
    }
    case 'distribute':
      return i18n.t('step.distribute');
    case 'collect-like-terms':
      return i18n.t('step.collect-like-terms');
    case 'cross-multiply':
      return i18n.t('step.cross-multiply');
    case 'square-both':
      return i18n.t('step.square-both');
    case 'factor':
      return i18n.t('step.factor');
    case 'apply-quadratic-formula':
      return i18n.t('step.quadratic-formula');
    case 'isolate-variable':
      return i18n.t('step.isolate-variable');
    case 'extraneous-root-removed':
      return i18n.t('step.extraneous-root-removed');
  }
}

/**
 * Describe a FactorStep with i18n support
 */
export function describeFactorStep(step: FactorStep, i18n: DescribeI18n): string {
  switch (step.kind) {
    case 'gcd-extracted':
      return i18n.t('step.factor-gcd');
    case 'rational-root-found': {
      const rootStr = step.root.toString();
      return `${i18n.t('step.factor-rational-root')} ${rootStr}`.trim();
    }
    case 'grouping':
      return i18n.t('step.factor-grouping');
    case 'special-product': {
      const form = step.form;
      switch (form) {
        case 'difference-of-squares':
          return i18n.t('step.factor-difference-of-squares');
        case 'sum-of-cubes':
          return i18n.t('step.factor-sum-of-cubes');
        case 'difference-of-cubes':
          return i18n.t('step.factor-difference-of-cubes');
        case 'perfect-square':
          return i18n.t('step.factor-perfect-square');
        default:
          return i18n.t('step.factor-special-product');
      }
    }
    case 'quadratic-formula':
      return i18n.t('step.factor-quadratic-formula');
    case 'irreducible':
      return i18n.t('step.factor-irreducible');
  }
}

/**
 * Describe a RewriteStep with i18n support
 */
export function describeRewriteStep(step: RewriteStep, i18n: DescribeI18n): string {
  const key = `step.${step.rule}`;
  return i18n.t(key);
}

/**
 * Describe a SystemStep with i18n support
 */
export function describeSystemStep(step: SystemStep, i18n: DescribeI18n): string {
  switch (step.rule) {
    case 'substitute':
      return i18n.t('step.system-substitute');
    case 'row-swap':
      return i18n.t('step.system-row-swap');
    case 'row-scale':
      return i18n.t('step.system-row-scale');
    case 'row-add':
      return i18n.t('step.system-row-add');
  }
}

/**
 * Describe an InequalityStep with i18n support
 */
export function describeInequalityStep(step: InequalityStep, i18n: DescribeI18n): string {
  switch (step.rule) {
    case 'move-to-one-side':
      return i18n.t('step.inequality-move-to-one-side');
    case 'factor':
      return i18n.t('step.inequality-factor');
    case 'identify-critical-points':
      return i18n.t('step.inequality-critical-points');
    case 'identify-asymptotes':
      return i18n.t('step.inequality-asymptotes');
    case 'evaluate-sign-chart':
      return i18n.t('step.inequality-sign-chart');
    case 'read-off-intervals':
      return i18n.t('step.inequality-read-intervals');
  }
}

/**
 * Format a value (Expr or Fraction) for display
 */
function formatValue(val: Expr | Fraction): string {
  if (val && typeof val === 'object') {
    if ('kind' in val) {
      // It's an Expr
      return format(val);
    }
    // It's a Fraction
    return val.toString();
  }
  return String(val);
}
